import { format, parse, isValid, Locale } from "date-fns";
import { de, enGB } from "date-fns/locale";
import { IntlShape } from "react-intl";
import { MIN_VALID_DATE_YEAR, MAX_VALID_DATE_YEAR } from "@/constants/dates";

/** Locale used for masked date input — must match mask order and parse format. */
export function getDateInputLocale(intl: IntlShape): Locale {
  return intl.locale.toLowerCase().startsWith("de") ? de : enGB;
}

export function isValidDisplayDate(date: Date | null | undefined): boolean {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return false;
  }

  const year = date.getFullYear();
  return year >= MIN_VALID_DATE_YEAR && year <= MAX_VALID_DATE_YEAR;
}

/** Rebuilds a date using its UTC year/month/day as local components. */
function toLocalCalendarEquivalent(date: Date): Date {
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

export function getDateInputMask(intl: IntlShape): string {
  return intl.locale.toLowerCase().startsWith("de")
    ? "00.00.0000"
    : "00/00/0000";
}

export function isCompleteDateInput(value: string): boolean {
  return /^\d{2}([/.])\d{2}\1\d{4}$/.test(value.trim());
}

export function getDateTimestamp(date: Date | null | undefined): number | undefined {
  if (!isValidDisplayDate(date)) return undefined;
  return date!.getTime();
}

export function formatDateInput(intl: IntlShape, date?: Date | string | null): string {
  if (!date) return "";

  const dateObj = typeof date === "string" ? new Date(date) : date;
  if (!isValidDisplayDate(dateObj)) {
    return "";
  }

  return format(toLocalCalendarEquivalent(dateObj), "P", { locale: getDateInputLocale(intl) });
}

export function parseDateInput(intl: IntlShape, value: string): Date | null {
  const trimmed = value.trim();
  if (!trimmed || !isCompleteDateInput(trimmed)) return null;

  const parsed = parse(trimmed, "P", new Date(), { locale: getDateInputLocale(intl) });
  if (!isValid(parsed) || !isValidDisplayDate(parsed)) {
    return null;
  }

  return parsed;
}
